// Localização: frontend/src/components/Financeiro/FloatingCalendarFilter.jsx
// Filtro de Calendário Flutuante para Dashboard

import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, X, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

// Função auxiliar para parsear data localmente
const parseLocalDateString = (dateString) => {
    if (!dateString) return null;
    const match = String(dateString).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;
    const [, year, month, day] = match;
    return new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
};

// Função auxiliar para formatar data
const formatDateISO = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// Componente de Calendário de Range (apenas o calendário, sem input trigger)
const RangeCalendarModal = ({ startDate, endDate, onRangeChange, onClose }) => {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const initialStart = startDate ? parseLocalDateString(startDate) : null;
    const initialEnd = endDate ? parseLocalDateString(endDate) : null;
    const [rangeStart, setRangeStart] = useState(initialStart);
    const [rangeEnd, setRangeEnd] = useState(initialEnd);
    const calendarRef = useRef(null);
    const userChangeRef = useRef(false);
    const rangeStartRef = useRef(initialStart);
    const rangeEndRef = useRef(initialEnd);

    // Manter refs atualizados
    useEffect(() => {
        rangeStartRef.current = rangeStart;
        rangeEndRef.current = rangeEnd;
    }, [rangeStart, rangeEnd]);

    // Atualizar estados quando props mudarem externamente (não por mudança do usuário)
    useEffect(() => {
        // Se foi uma mudança do usuário, aguardar confirmação
        if (userChangeRef.current) {
            // Se as props foram atualizadas para valores vazios, significa que foi um "limpar"
            if ((!startDate || startDate.trim() === '') && (!endDate || endDate.trim() === '')) {
                setRangeStart(null);
                setRangeEnd(null);
                userChangeRef.current = false;
                return;
            }
            
            // Verificar se as props confirmam a mudança do usuário usando os refs (valores atuais)
            const currentStartStr = rangeStartRef.current ? formatDateISO(rangeStartRef.current) : '';
            const currentEndStr = rangeEndRef.current ? formatDateISO(rangeEndRef.current) : '';
            
            if (startDate === currentStartStr && endDate === currentEndStr) {
                // Props confirmaram a mudança do usuário
                userChangeRef.current = false;
            }
            return;
        }
        
        // Atualizar estados quando props mudarem externamente
        if (startDate && startDate.trim()) {
            const parsed = parseLocalDateString(startDate);
            const currentStartStr = rangeStartRef.current ? formatDateISO(rangeStartRef.current) : '';
            if (startDate !== currentStartStr) {
                setRangeStart(parsed);
            }
        } else if (rangeStartRef.current !== null) {
            setRangeStart(null);
        }
        
        if (endDate && endDate.trim()) {
            const parsed = parseLocalDateString(endDate);
            const currentEndStr = rangeEndRef.current ? formatDateISO(rangeEndRef.current) : '';
            if (endDate !== currentEndStr) {
                setRangeEnd(parsed);
            }
        } else if (rangeEndRef.current !== null) {
            setRangeEnd(null);
        }
    }, [startDate, endDate]);

    // Fechar ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (calendarRef.current && !calendarRef.current.contains(event.target)) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [onClose]);

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();

        const days = [];
        
        const prevMonth = new Date(year, month, 0);
        const prevMonthDays = prevMonth.getDate();
        for (let i = startingDayOfWeek - 1; i >= 0; i--) {
            days.push({
                date: new Date(year, month - 1, prevMonthDays - i),
                isCurrentMonth: false
            });
        }

        for (let day = 1; day <= daysInMonth; day++) {
            days.push({
                date: new Date(year, month, day),
                isCurrentMonth: true
            });
        }

        const remainingDays = 42 - days.length;
        for (let day = 1; day <= remainingDays; day++) {
            days.push({
                date: new Date(year, month + 1, day),
                isCurrentMonth: false
            });
        }

        return days;
    };

    const handleDateSelect = (date) => {
        // Marcar que é uma mudança do usuário
        userChangeRef.current = true;
        
        // Se não há rangeStart ou se já há ambos rangeStart e rangeEnd, começar nova seleção
        if (!rangeStart || (rangeStart && rangeEnd)) {
            setRangeStart(date);
            setRangeEnd(null);
        } else {
            // Completar a seleção do range
            let finalStart = rangeStart;
            let finalEnd = date;
            
            if (date < rangeStart) {
                finalStart = date;
                finalEnd = rangeStart;
            }
            
            setRangeStart(finalStart);
            setRangeEnd(finalEnd);
            
            // Aplicar automaticamente quando ambas as datas forem selecionadas
            // Mas não fechar o calendário - permitir edição contínua
            if (onRangeChange) {
                onRangeChange(formatDateISO(finalStart), formatDateISO(finalEnd));
            }
        }
    };

    const navigateMonth = (direction) => {
        setCurrentMonth(prev => {
            const newDate = new Date(prev);
            newDate.setMonth(prev.getMonth() + direction);
            return newDate;
        });
    };

    const goToToday = () => {
        setCurrentMonth(new Date());
    };

    const isToday = (date) => {
        const today = new Date();
        return date.toDateString() === today.toDateString();
    };

    const isSelected = (date) => {
        if (!rangeStart) return false;
        const dateStr = formatDateISO(date);
        return dateStr === formatDateISO(rangeStart) || 
               (rangeEnd && dateStr === formatDateISO(rangeEnd));
    };

    const isInRange = (date) => {
        if (!rangeStart || !rangeEnd) return false;
        const dateStr = formatDateISO(date);
        const startStr = formatDateISO(rangeStart);
        const endStr = formatDateISO(rangeEnd);
        return dateStr >= startStr && dateStr <= endStr;
    };

    const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
    const months = [
        'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
        'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];

    const days = getDaysInMonth(currentMonth);

    return (
        <div 
            ref={calendarRef}
            className="bg-white rounded-xl shadow-2xl border border-gray-200 p-6 max-w-md w-full mx-4 animate-in slide-in-from-top-2 duration-200"
            onClick={(e) => e.stopPropagation()}
        >
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Selecionar Período</h3>
                <button
                    onClick={onClose}
                    className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
                    title="Fechar"
                >
                    <X className="h-5 w-5" />
                </button>
            </div>
            <p className="text-xs text-gray-500 mb-4">
                {!rangeStart 
                    ? 'Clique no dia inicial para começar'
                    : !rangeEnd 
                        ? 'Agora clique no dia final para completar o período'
                        : `Período: ${rangeStart.toLocaleDateString('pt-BR')} - ${rangeEnd.toLocaleDateString('pt-BR')}`
                }
            </p>

            {/* Navegação do Mês */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        navigateMonth(-1);
                    }}
                    className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                >
                    <ChevronLeft className="h-5 w-5 text-gray-600" />
                </button>
                <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-gray-900">
                        {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                    </h3>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            goToToday();
                        }}
                        className="px-2 py-1 text-xs font-medium text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                    >
                        Hoje
                    </button>
                </div>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        navigateMonth(1);
                    }}
                    className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                >
                    <ChevronRight className="h-5 w-5 text-gray-600" />
                </button>
            </div>

            {/* Grid de Dias */}
            <div className="p-4">
                {/* Dias da Semana */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                    {weekDays.map(day => (
                        <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                            {day}
                        </div>
                    ))}
                </div>

                {/* Dias do Calendário */}
                <div className="grid grid-cols-7 gap-1">
                    {days.map((dayObj, index) => {
                        const { date, isCurrentMonth } = dayObj;
                        const isSelectedDay = isSelected(date);
                        const isTodayDay = isToday(date);
                        const inRange = isInRange(date);

                        return (
                            <button
                                key={index}
                                onClick={(e) => {
                                    e.stopPropagation(); // Evitar propagação do evento
                                    handleDateSelect(date);
                                }}
                                className={`
                                    aspect-square p-1 text-sm rounded-lg transition-all duration-150
                                    ${!isCurrentMonth ? 'text-gray-300' : 'text-gray-700'}
                                    ${isTodayDay && !isSelectedDay ? 'bg-emerald-50 text-emerald-600 font-semibold' : ''}
                                    ${isSelectedDay ? 'bg-emerald-600 text-white font-semibold shadow-md' : ''}
                                    ${inRange && !isSelectedDay ? 'bg-emerald-100 text-emerald-700' : ''}
                                    ${isCurrentMonth && !isSelectedDay && !inRange && !isTodayDay 
                                        ? 'hover:bg-gray-100' : ''}
                                `}
                            >
                                {date.getDate()}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Botão Limpar */}
            {(rangeStart || rangeEnd) && (
                <div className="p-4 border-t border-gray-100">
                    <button
                        onClick={(e) => {
                            e.stopPropagation(); // Evitar propagação do evento
                            // Marcar que é uma mudança do usuário
                            userChangeRef.current = true;
                            // Limpar estados locais imediatamente
                            setRangeStart(null);
                            setRangeEnd(null);
                            // Notificar componente pai para limpar
                            if (onRangeChange) {
                                onRangeChange('', '');
                            }
                            // Não fechar o calendário - permitir nova seleção
                        }}
                        className="w-full px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                    >
                        Limpar Seleção
                    </button>
                </div>
            )}
        </div>
    );
};

/**
 * @component FloatingCalendarFilter
 * @description Filtro de calendário flutuante que acompanha a rolagem
 * @param {string} periodo - Período selecionado (dia, semana, mes)
 * @param {function} onPeriodoChange - Callback quando período muda
 * @param {string} startDate - Data inicial
 * @param {string} endDate - Data final
 * @param {function} onDateRangeChange - Callback quando range de datas muda
 * @param {Array} status - Array de status selecionados
 * @param {function} onStatusChange - Callback quando status muda
 * @param {string} type - Tipo de conta ('pagar' ou 'receber') para definir status disponíveis
 * @param {boolean} showStatus - Se deve mostrar filtros de status
 * @param {boolean} noNegativeMargin - Se true, remove margens negativas (útil para uso em containers com padding)
 * @param {string} customZIndex - Z-index customizado para sobrescrever o padrão (ex: 'z-0', 'z-10')
 * @param {boolean} isInDropdown - Se true, remove classes sticky e ajusta para uso dentro de dropdown
 * @param {boolean} showOnlyContent - Se true, mostra apenas o conteúdo do dropdown sem o botão trigger
 */
const FloatingCalendarFilter = ({
    periodo = 'dia',
    onPeriodoChange,
    startDate = '',
    endDate = '',
    onDateRangeChange,
    status = [],
    onStatusChange,
    type = 'pagar',
    showStatus = false,
    noNegativeMargin = false,
    customZIndex = null,
    isInDropdown = false,
    showOnlyContent = false,
    onClose = null
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [portalContainer, setPortalContainer] = useState(null);
    const filterRef = useRef(null);
    const calendarPortalRef = useRef(null);
    
    // Se showOnlyContent for true, sempre mostrar o conteúdo (como se estivesse sempre aberto)
    const isOpenState = showOnlyContent ? true : isOpen;
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);

    // Criar container para portal do calendário usando useLayoutEffect para garantir sincronização
    useLayoutEffect(() => {
        if (showAdvanced) {
            // Sempre remover container existente antes de criar um novo (evita conflitos)
            const existingContainer = document.getElementById('calendar-portal-container');
            if (existingContainer && existingContainer.parentNode) {
                document.body.removeChild(existingContainer);
            }
            
            // Criar novo container
            const container = document.createElement('div');
            container.id = 'calendar-portal-container';
            container.className = 'fixed inset-0 z-[10000] flex items-center justify-center bg-black/20 backdrop-blur-sm';
            document.body.appendChild(container);
            
            calendarPortalRef.current = container;
            
            // Atualizar estado de forma síncrona para garantir que o React detecte a mudança
            setPortalContainer(container);

            // Fechar ao clicar no backdrop
            const handleBackdropClick = (e) => {
                if (e.target === container) {
                    setShowAdvanced(false);
                }
            };
            container.addEventListener('click', handleBackdropClick);

            return () => {
                container.removeEventListener('click', handleBackdropClick);
                // Remover container ao desmontar
                if (container && container.parentNode && document.body.contains(container)) {
                    document.body.removeChild(container);
                }
                calendarPortalRef.current = null;
                setPortalContainer(null);
            };
        } else {
            // Remover container quando fechar
            const container = document.getElementById('calendar-portal-container');
            if (container && container.parentNode && document.body.contains(container)) {
                document.body.removeChild(container);
            }
            calendarPortalRef.current = null;
            setPortalContainer(null);
        }
    }, [showAdvanced]);

    // Função auxiliar para formatar data localmente (sem conversão UTC)
    // Evita problemas de timezone que podem resultar em datas incorretas
    const formatLocalDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const handleQuickPeriodChange = (newPeriodo) => {
        const today = new Date();
        let startDateStr = '';
        let endDateStr = '';

        if (newPeriodo === 'dia') {
            // Dia: apenas o dia atual
            startDateStr = formatLocalDate(today);
            endDateStr = formatLocalDate(today);
        } else if (newPeriodo === 'semana') {
            // Semana: do início da semana (domingo) até o fim (sábado)
            const dayOfWeek = today.getDay(); // 0 = domingo, 6 = sábado
            const startOfWeek = new Date(today);
            startOfWeek.setDate(today.getDate() - dayOfWeek);
            const endOfWeek = new Date(startOfWeek);
            endOfWeek.setDate(startOfWeek.getDate() + 6);
            
            startDateStr = formatLocalDate(startOfWeek);
            endDateStr = formatLocalDate(endOfWeek);
        } else if (newPeriodo === 'mes') {
            // Mês: do primeiro ao último dia do mês atual
            const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
            const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
            
            startDateStr = formatLocalDate(firstDay);
            endDateStr = formatLocalDate(lastDay);
        }

        // Verificar se os valores realmente mudaram antes de chamar
        if (startDate !== startDateStr || endDate !== endDateStr) {
            onDateRangeChange(startDateStr, endDateStr);
        }
        
        // Só chamar onPeriodoChange se o período realmente mudou
        if (periodo !== newPeriodo) {
            onPeriodoChange(newPeriodo);
        }
        
        // Abrir o painel avançado para mostrar as datas
        if (!showAdvanced) {
            setShowAdvanced(true);
        }
    };

    const handleSpecificDayFilter = (dayType) => {
        const today = new Date();
        const targetDate = new Date();
        
        if (dayType === 'hoje') {
            targetDate.setDate(today.getDate());
        } else if (dayType === 'ontem') {
            targetDate.setDate(today.getDate() - 1);
        } else if (dayType === 'amanha') {
            targetDate.setDate(today.getDate() + 1);
        }
        
        const dateStr = formatLocalDate(targetDate);
        
        // Verificar se os valores realmente mudaram antes de chamar
        if (startDate !== dateStr || endDate !== dateStr) {
            onDateRangeChange(dateStr, dateStr);
        }
        
        // Só chamar onPeriodoChange se o período realmente mudou
        if (periodo !== 'custom') {
            onPeriodoChange('custom');
        }
        
        // Abrir o painel avançado para mostrar as datas
        if (!showAdvanced) {
            setShowAdvanced(true);
        }
    };

    const handleNextDaysFilter = (days) => {
        const today = new Date();
        const endDate = new Date();
        endDate.setDate(today.getDate() + days);
        
        const startDateStr = formatLocalDate(today);
        const endDateStr = formatLocalDate(endDate);
        
        // Verificar se os valores realmente mudaram antes de chamar
        if (startDate !== startDateStr || endDate !== endDateStr) {
            onDateRangeChange(startDateStr, endDateStr);
        }
        
        // Só chamar onPeriodoChange se o período realmente mudou
        if (periodo !== 'custom') {
            onPeriodoChange('custom');
        }
        
        // Abrir o painel avançado para mostrar as datas
        if (!showAdvanced) {
            setShowAdvanced(true);
        }
    };

    const handleLastDaysFilter = (days) => {
        const today = new Date();
        const startDateCalc = new Date();
        startDateCalc.setDate(today.getDate() - days);
        
        const startDateStr = formatLocalDate(startDateCalc);
        const endDateStr = formatLocalDate(today);
        
        // Verificar se os valores realmente mudaram antes de chamar
        if (startDate !== startDateStr || endDate !== endDateStr) {
            onDateRangeChange(startDateStr, endDateStr);
        }
        
        // Só chamar onPeriodoChange se o período realmente mudou
        if (periodo !== 'custom') {
            onPeriodoChange('custom');
        }
        
        // Abrir o painel avançado para mostrar as datas
        if (!showAdvanced) {
            setShowAdvanced(true);
        }
    };

    const isNextDaysActive = (days) => {
        if (!startDate || !endDate || periodo !== 'custom') return false;
        
        const today = new Date();
        const expectedEnd = new Date();
        expectedEnd.setDate(today.getDate() + days);
        
        const todayStr = formatLocalDate(today);
        const expectedEndStr = formatLocalDate(expectedEnd);
        
        return startDate === todayStr && endDate === expectedEndStr;
    };

    const isLastDaysActive = (days) => {
        if (!startDate || !endDate || periodo !== 'custom') return false;
        
        const today = new Date();
        const expectedStart = new Date();
        expectedStart.setDate(today.getDate() - days);
        
        const todayStr = formatLocalDate(today);
        const expectedStartStr = formatLocalDate(expectedStart);
        
        return startDate === expectedStartStr && endDate === todayStr;
    };

    const isPeriodActive = (period) => {
        if (!startDate || !endDate) return periodo === period && !startDate && !endDate;
        
        const today = new Date();
        let expectedStart = '';
        let expectedEnd = '';

        if (period === 'dia') {
            expectedStart = formatLocalDate(today);
            expectedEnd = formatLocalDate(today);
        } else if (period === 'semana') {
            const dayOfWeek = today.getDay();
            const startOfWeek = new Date(today);
            startOfWeek.setDate(today.getDate() - dayOfWeek);
            const endOfWeek = new Date(startOfWeek);
            endOfWeek.setDate(startOfWeek.getDate() + 6);
            expectedStart = formatLocalDate(startOfWeek);
            expectedEnd = formatLocalDate(endOfWeek);
        } else if (period === 'mes') {
            const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
            const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
            expectedStart = formatLocalDate(firstDay);
            expectedEnd = formatLocalDate(lastDay);
        }

        return periodo === period && startDate === expectedStart && endDate === expectedEnd;
    };

    const isSpecificDayActive = (dayType) => {
        if (!startDate || !endDate || startDate !== endDate || periodo !== 'custom') return false;
        
        const today = new Date();
        const targetDate = new Date();
        
        if (dayType === 'hoje') {
            targetDate.setDate(today.getDate());
        } else if (dayType === 'ontem') {
            targetDate.setDate(today.getDate() - 1);
        } else if (dayType === 'amanha') {
            targetDate.setDate(today.getDate() + 1);
        }
        
        const targetDateStr = formatLocalDate(targetDate);
        return startDate === targetDateStr && endDate === targetDateStr;
    };

    // Fechar dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current && 
                buttonRef.current &&
                !dropdownRef.current.contains(event.target) &&
                !buttonRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Fechar ao pressionar Escape
    useEffect(() => {
        if (!isOpen) return;

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen]);

    // Função auxiliar para obter o texto do período selecionado
    const getPeriodLabel = () => {
        if (startDate && endDate) {
            if (startDate === endDate) {
                return new Date(startDate).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
            }
            return `${new Date(startDate).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })} - ${new Date(endDate).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}`;
        }
        
        if (isPeriodActive('semana')) return 'Esta Semana';
        if (isPeriodActive('mes')) return 'Este Mês';
        if (isSpecificDayActive('hoje')) return 'Hoje';
        if (isSpecificDayActive('ontem')) return 'Ontem';
        if (isSpecificDayActive('amanha')) return 'Amanhã';
        
        return 'Período';
    };

    const hasActiveFilters = startDate || endDate;
    
    // Função auxiliar para renderizar o portal do calendário
    const renderCalendarPortal = () => {
        if (!showAdvanced || !portalContainer || typeof document === 'undefined') {
            return null;
        }
        
        return createPortal(
            <RangeCalendarModal
                startDate={startDate}
                endDate={endDate}
                onRangeChange={(newStartDate, newEndDate) => {
                    onDateRangeChange(newStartDate, newEndDate);
                    // Não fechar automaticamente - permitir edição contínua do calendário
                    // O usuário pode continuar editando mesmo após selecionar ambas as datas
                }}
                onClose={() => setShowAdvanced(false)}
            />,
            portalContainer
        );
    };
    
    // Função auxiliar para renderizar o conteúdo do dropdown
    const renderDropdownContent = () => {
        const handleCloseOnSelect = showOnlyContent ? undefined : () => setIsOpen(false);
        
        return (
            <>
                {/* Dias Específicos */}
                <div className="mb-4">
                    <h3 className="text-xs font-semibold text-gray-700 mb-2">Dias</h3>
                    <div className="flex flex-wrap gap-1.5">
                        {['ontem', 'hoje', 'amanha'].map(day => (
                            <button
                                key={day}
                                onClick={() => {
                                    handleSpecificDayFilter(day);
                                    if (handleCloseOnSelect) handleCloseOnSelect();
                                }}
                                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200 border shadow-sm hover:shadow-md ${
                                    isSpecificDayActive(day)
                                        ? 'bg-emerald-100 text-emerald-600 border-emerald-300 border-[0.5px] shadow-md font-semibold'
                                        : 'bg-emerald-50 text-emerald-600 border-transparent hover:border-emerald-300 hover:bg-emerald-100'
                                }`}
                            >
                                {day === 'ontem' ? 'Ontem' : day === 'hoje' ? 'Hoje' : 'Amanhã'}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Períodos Rápidos */}
                <div className="mb-4">
                    <h3 className="text-xs font-semibold text-gray-700 mb-2">Períodos</h3>
                    <div className="flex flex-wrap gap-1.5">
                        {['semana', 'mes'].map(period => (
                            <button
                                key={period}
                                onClick={() => {
                                    handleQuickPeriodChange(period);
                                    if (handleCloseOnSelect) handleCloseOnSelect();
                                }}
                                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200 border shadow-sm hover:shadow-md ${
                                    isPeriodActive(period)
                                        ? 'bg-emerald-100 text-emerald-600 border-emerald-300 border-[0.5px] shadow-md font-semibold'
                                        : 'bg-emerald-50 text-emerald-600 border-transparent hover:border-emerald-300 hover:bg-emerald-100'
                                }`}
                            >
                                {period === 'semana' ? 'Semana' : 'Mês'}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Últimos Dias */}
                <div className="mb-4">
                    <h3 className="text-xs font-semibold text-gray-700 mb-2">Últimos</h3>
                    <div className="flex flex-wrap gap-1.5">
                        {[7, 30, 60].map(days => (
                            <button
                                key={days}
                                onClick={() => {
                                    handleLastDaysFilter(days);
                                    if (handleCloseOnSelect) handleCloseOnSelect();
                                }}
                                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200 border shadow-sm hover:shadow-md ${
                                    isLastDaysActive(days)
                                        ? 'bg-emerald-100 text-emerald-600 border-emerald-300 border-[0.5px] shadow-md font-semibold'
                                        : 'bg-emerald-50 text-emerald-600 border-transparent hover:border-emerald-300 hover:bg-emerald-100'
                                }`}
                            >
                                {days} dias
                            </button>
                        ))}
                    </div>
                </div>

                {/* Próximos Dias */}
                <div className="mb-4">
                    <h3 className="text-xs font-semibold text-gray-700 mb-2">Próximos</h3>
                    <div className="flex flex-wrap gap-1.5">
                        {[7, 30, 60, 90].map(days => (
                            <button
                                key={days}
                                onClick={() => {
                                    handleNextDaysFilter(days);
                                    if (handleCloseOnSelect) handleCloseOnSelect();
                                }}
                                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200 border shadow-sm hover:shadow-md ${
                                    isNextDaysActive(days)
                                        ? 'bg-emerald-100 text-emerald-600 border-emerald-300 border-[0.5px] shadow-md font-semibold'
                                        : 'bg-emerald-50 text-emerald-600 border-transparent hover:border-emerald-300 hover:bg-emerald-100'
                                }`}
                            >
                                {days} dias
                            </button>
                        ))}
                    </div>
                </div>

                {/* Calendário Avançado */}
                <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-xs font-semibold text-gray-700">Calendário</h3>
                        <button
                            onClick={() => setShowAdvanced(!showAdvanced)}
                            className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                        >
                            {showAdvanced ? 'Ocultar' : 'Mostrar'}
                        </button>
                    </div>
                </div>

                {/* Botão Limpar */}
                {(startDate || endDate) && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                        <button
                            onClick={() => {
                                // Só limpar se já houver valores
                                if (startDate || endDate) {
                                    onDateRangeChange('', '');
                                }
                                if (handleCloseOnSelect) handleCloseOnSelect();
                            }}
                            className="w-full px-3 py-2 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors flex items-center justify-center gap-2"
                        >
                            <X className="h-3.5 w-3.5" />
                            Limpar Filtros
                        </button>
                    </div>
                )}
            </>
        );
    };
    
    // Classes de posicionamento: sticky para acompanhar scroll, mas não fixo
    const zIndexClass = customZIndex || 'z-10';
    
    // Se estiver dentro de dropdown, não usar sticky
    const marginClasses = isInDropdown
        ? `${zIndexClass}`
        : noNegativeMargin 
            ? `sticky top-20 lg:top-16 ${zIndexClass} mb-4` 
            : `sticky top-20 lg:top-16 ${zIndexClass} mb-4 -mx-6 sm:-mx-8 lg:-mx-10 px-6 sm:px-8 lg:px-10`;

    // Se showOnlyContent for true, renderizar apenas o conteúdo do dropdown
    if (showOnlyContent) {
        return (
            <>
                <div 
                    ref={filterRef}
                    className="bg-white rounded-xl shadow-2xl border border-gray-200 p-4 max-h-[calc(100vh-6rem)] sm:max-h-[80vh] overflow-y-auto relative z-[100]"
                >
                    {/* Botão de Fechar */}
                    <button
                        onClick={() => {
                            if (onClose) {
                                onClose();
                            }
                        }}
                        className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600 z-10"
                        title="Fechar filtro"
                        aria-label="Fechar filtro"
                    >
                        <X className="h-4 w-4" />
                    </button>
                    {/* Conteúdo do dropdown - mesmo código abaixo */}
                    {renderDropdownContent()}
                </div>
                
                {/* Portal do Calendário de Range - Renderizado fora do componente */}
                {renderCalendarPortal()}
            </>
        );
    }

    return (
        <>
            <div 
                ref={filterRef}
                className={`${marginClasses} inline-flex items-center`}
            >
                <div className="relative">
                    {/* Botão Trigger */}
                    {!showOnlyContent && (
                        <button
                            ref={buttonRef}
                            onClick={() => setIsOpen(!isOpen)}
                            className={`flex items-center justify-center gap-2 px-3 py-2 sm:px-4 sm:py-2.5 text-xs sm:text-sm font-medium rounded-lg transition-all duration-200 border shadow-sm hover:shadow-md whitespace-nowrap ${
                                hasActiveFilters || isOpen
                                    ? 'bg-emerald-100 text-emerald-600 border-emerald-300 border-[0.5px] shadow-md font-semibold'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gradient-to-br hover:from-emerald-50 hover:to-emerald-100 hover:border-emerald-400'
                            }`}
                        >
                            <Calendar className="h-4 w-4" />
                            <span>{getPeriodLabel()}</span>
                            <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                        </button>
                    )}

                    {/* Dropdown Menu */}
                    {isOpen && (
                        <div
                            ref={dropdownRef}
                            className="absolute top-full left-0 mt-2 z-[9999] min-w-[320px] max-w-[500px] bg-white rounded-xl shadow-2xl border border-gray-200 p-4 animate-fadeIn max-h-[80vh] overflow-y-auto"
                        >
                            {renderDropdownContent()}
                        </div>
                    )}
                </div>
            </div>

            {/* Portal do Calendário de Range - Renderizado fora do componente */}
            {renderCalendarPortal()}
        </>
    );
};

export default FloatingCalendarFilter;

